import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../Reveal";
import api, { formatApiError } from "../../lib/api";

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      setDone(true);
      toast.success(data.already ? "You are already in the Circle" : "Welcome to the Locka Circle", {
        description: data.email_sent ? "A golden welcome note is on its way to your inbox." : "First look at every drop, guaranteed.",
      });
    } catch (err) {
      toast.error("Could not subscribe", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="circle" className="relative overflow-hidden bg-burgundy py-28 text-white md:py-40" data-testid="newsletter-section">
      <span aria-hidden="true" className="text-outline-cream pointer-events-none absolute -left-6 bottom-0 select-none font-serif text-[15vw] font-bold italic leading-none opacity-70">
        Circle
      </span>
      <div className="relative mx-auto max-w-3xl px-6 text-center md:px-10">
        <Reveal>
          <p className="mb-4 text-[0.68rem] font-bold uppercase tracking-[0.34em] text-gold-soft">Private previews · First access · Styling notes</p>
          <h2 className="font-serif text-4xl leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="newsletter-headline">
            Join the <em className="text-gold">Locka Circle</em>
          </h2>
          <p className="mx-auto mt-6 max-w-md leading-relaxed text-white/70">
            Limited editions vanish in hours. Circle members hear first — and step first.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mx-auto mt-12 flex max-w-md items-center justify-center gap-3 border border-gold/50 bg-white/10 px-8 py-5"
              data-testid="newsletter-success"
            >
              <Check className="h-5 w-5 text-gold" strokeWidth={2} />
              <span className="font-serif text-xl italic">You are in. Watch your inbox for gold.</span>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mx-auto mt-12 flex max-w-xl flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@finest-address.com"
                data-testid="newsletter-email-input"
                className="h-14 flex-1 border border-white/30 bg-white/10 px-6 text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="group relative h-14 overflow-hidden bg-gold px-8 text-[0.7rem] font-bold uppercase tracking-[0.26em] text-[#0B101E] disabled:opacity-60"
              >
                <span className="absolute inset-0 -translate-x-full bg-[#0B101E] transition-transform duration-500 ease-out group-hover:translate-x-0" />
                <span className="relative flex items-center gap-2 transition-colors duration-500 group-hover:text-gold">
                  {loading ? "Stepping in…" : "Step In — Subscribe Now"} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </span>
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
